import React, { useState, useEffect, useMemo } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, Container, Box, IconButton, Grid, Snackbar, Alert, CircularProgress, Paper, Skeleton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import DescriptionIcon from '@mui/icons-material/Description';
import FeedbackIcon from '@mui/icons-material/Feedback';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { alpha } from '@mui/material/styles';
import { db } from '../firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

const MotionBox = motion(Box);

const COLORS = {
    success: '#22C55E',
    warning: '#F59E0B',
    expense: '#EF4444',
    axis: '#A1A1AA',
    grid: '#3F3F46',
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <Box sx={{ bgcolor: '#1a1a1b', border: '1px solid #3F3F46', borderRadius: 1, p: 1.5 }}>
                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>{label}</Typography>
                {payload.map((entry, index) => (
                    <Typography key={index} variant="body2" sx={{ color: entry.color }}>
                        {entry.name}: ${parseFloat(entry.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                ))}
            </Box>
        );
    }
    return null;
};

function Home() {
    const [openAbout, setOpenAbout] = useState(false);
    const [openFeedback, setOpenFeedback] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    const [loading, setLoading] = useState(true);

    const [residents, setResidents] = useState([]);
    const [payments, setPayments] = useState([]);
    const [bills, setBills] = useState([]);
    const [adjustments, setAdjustments] = useState([]);

    const fadeIn = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { duration: 0.3 }
        }
    };

    // Current period derivation (same logic as Payments.js)
    const today = useMemo(() => new Date(), []);
    const currentYear = today.getFullYear();
    const periodHalf = today.getMonth() < 6 ? 1 : 2;
    const periodStart = useMemo(() => new Date(currentYear, periodHalf === 1 ? 0 : 6, 1), [currentYear, periodHalf]);

    const periodLabel = periodHalf === 1
        ? `Jan 1 – Jun 30, ${currentYear}`
        : `Jul 1 – Dec 30, ${currentYear}`;

    // Fetch all data on mount
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [residentsSnap, paymentsSnap, billsSnap, adjustmentsSnap] = await Promise.all([
                    getDocs(collection(db, 'residents')),
                    getDocs(collection(db, 'payments')),
                    getDocs(collection(db, 'bills')),
                    getDocs(collection(db, 'manualLedgerAdjustments')),
                ]);

                setResidents(residentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                setPayments(paymentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                setBills(billsSnap.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        ...data,
                        datePaid: data.datePaid?.toDate?.() || (data.datePaid instanceof Date ? data.datePaid : null),
                    };
                }));
                setAdjustments(adjustmentsSnap.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        ...data,
                        date: data.date?.toDate?.() || (data.date instanceof Date ? data.date : null),
                    };
                }));
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // KPI calculations
    const currentPeriodPayments = useMemo(() => {
        return payments.filter(p => {
            const pStart = p.periodStart?.toDate?.() || p.periodStart;
            if (!pStart || !(pStart instanceof Date)) return false;
            return pStart.getFullYear() === periodStart.getFullYear() &&
                   pStart.getMonth() === periodStart.getMonth();
        });
    }, [payments, periodStart]);

    const paidCount = useMemo(() => currentPeriodPayments.filter(p => p.isPaid).length, [currentPeriodPayments]);
    const totalCount = currentPeriodPayments.length;
    const collectionPct = totalCount > 0 ? Math.round((paidCount / totalCount) * 100) : 0;

    const hoaBalance = useMemo(() => {
        const totalPayments = payments
            .filter(p => p.isPaid)
            .reduce((acc, p) => acc + parseFloat(p.paymentAmount || 0), 0);
        const totalBills = bills
            .filter(b => b.paid)
            .reduce((acc, b) => acc + parseFloat(b.amount || 0), 0);
        const totalAdjustments = adjustments.reduce((acc, adj) => {
            if (adj.type === 'manual') {
                return adj.subType === 'credit'
                    ? acc + parseFloat(adj.amount || 0)
                    : acc - parseFloat(adj.amount || 0);
            }
            return acc;
        }, 0);
        return totalPayments - totalBills + totalAdjustments;
    }, [payments, bills, adjustments]);

    const outstandingBills = useMemo(() => {
        const unpaid = bills.filter(b => !b.paid);
        const total = unpaid.reduce((acc, b) => acc + parseFloat(b.amount || 0), 0);
        return { count: unpaid.length, total };
    }, [bills]);

    // Chart data
    const donutData = useMemo(() => [
        { name: 'Paid', value: paidCount },
        { name: 'Pending', value: Math.max(totalCount - paidCount, 0) },
    ], [paidCount, totalCount]);

    const monthlyData = useMemo(() => {
        const months = [];
        for (let i = 5; i >= 0; i--) {
            const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
            months.push({
                month: d.toLocaleString('default', { month: 'short' }),
                year: d.getFullYear(),
                monthIndex: d.getMonth(),
                Income: 0,
                Expenses: 0,
            });
        }

        payments.filter(p => p.isPaid).forEach(p => {
            const paidDate = p.paidDate?.toDate?.() || (p.paidDate instanceof Date ? p.paidDate : null);
            if (!paidDate) return;
            const entry = months.find(m => m.monthIndex === paidDate.getMonth() && m.year === paidDate.getFullYear());
            if (entry) entry.Income += parseFloat(p.paymentAmount || 0);
        });

        bills.filter(b => b.paid).forEach(b => {
            const datePaid = b.datePaid;
            if (!datePaid) return;
            const entry = months.find(m => m.monthIndex === datePaid.getMonth() && m.year === datePaid.getFullYear());
            if (entry) entry.Expenses += parseFloat(b.amount || 0);
        });

        return months;
    }, [payments, bills, today]);

    // Handlers (preserved from original)
    const handleOpenAbout = () => setOpenAbout(true);
    const handleCloseAbout = () => setOpenAbout(false);
    const handleOpenFeedback = () => setOpenFeedback(true);
    const handleCloseFeedback = () => {
        setOpenFeedback(false);
        setFeedback('');
        setIsSubmittingFeedback(false);
    };

    const handleSubmitFeedback = async () => {
        if (!feedback.trim()) {
            setSnackbar({
                open: true,
                message: 'Please enter your feedback before submitting',
                severity: 'warning'
            });
            return;
        }

        setIsSubmittingFeedback(true);

        try {
            const response = await fetch('https://us-central1-hoa-management24.cloudfunctions.net/sendFeedback', {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    feedback: feedback
                })
            });

            if (response.ok) {
                setSnackbar({
                    open: true,
                    message: 'Thank you for your feedback!',
                    severity: 'success'
                });
                setFeedback('');
                handleCloseFeedback();
            } else {
                throw new Error('Failed to send feedback');
            }
        } catch (error) {
            console.error('Failed to send feedback:', error);
            setSnackbar({
                open: true,
                message: 'Failed to send feedback. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar({ ...snackbar, open: false });
    };

    const openTermsOfService = () => {
        window.open('/terms.pdf', '_blank');
    };

    const formatCurrency = (value) =>
        `$${parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const kpiCards = [
        {
            title: 'Total Residents',
            value: residents.length,
            subtitle: 'Active accounts',
            icon: <PeopleIcon sx={{ fontSize: 28 }} />,
        },
        {
            title: 'Collection Rate',
            value: `${paidCount} of ${totalCount} paid (${collectionPct}%)`,
            subtitle: periodLabel,
            icon: <PaymentIcon sx={{ fontSize: 28 }} />,
        },
        {
            title: 'HOA Balance',
            value: formatCurrency(hoaBalance),
            subtitle: 'Net balance',
            icon: <AccountBalanceIcon sx={{ fontSize: 28 }} />,
        },
        {
            title: 'Outstanding Bills',
            value: formatCurrency(outstandingBills.total),
            subtitle: `${outstandingBills.count} unpaid bill${outstandingBills.count !== 1 ? 's' : ''}`,
            icon: <ReceiptLongIcon sx={{ fontSize: 28 }} />,
        },
    ];

    return (
        <>
            <Box
                sx={{
                    minHeight: 'calc(100vh - 64px)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 6,
                    px: 3,
                    backgroundColor: 'background.default'
                }}
            >
            <Container maxWidth="lg">
                <MotionBox
                    initial="hidden"
                    animate="visible"
                    variants={fadeIn}
                >
                    {/* Header */}
                    <Box sx={{ mb: 6, textAlign: 'center' }}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                letterSpacing: '-0.02em',
                                mb: 2,
                                color: 'text.primary'
                            }}
                        >
                            myHOA Portal
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'grey.500',
                                maxWidth: '600px',
                                mx: 'auto',
                                mb: 2
                            }}
                        >
                            Financial overview and community metrics at a glance
                        </Typography>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: 2,
                                py: 0.75,
                                borderRadius: 2,
                                bgcolor: 'rgba(255, 255, 255, 0.04)',
                                border: '1px solid',
                                borderColor: 'grey.800',
                                mb: 4
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'grey.400',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        bgcolor: 'grey.500'
                                    }}
                                />
                                Licensed and custom built for Tuscany Manor
                            </Typography>
                        </Box>
                    </Box>

                    {/* KPI Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {loading ? (
                            [0, 1, 2, 3].map(i => (
                                <Grid item xs={12} sm={6} md={3} key={i}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            borderRadius: 3,
                                            bgcolor: theme => alpha(theme.palette.primary.main, 0.03),
                                            border: '1px solid',
                                            borderColor: theme => alpha(theme.palette.primary.main, 0.08),
                                        }}
                                    >
                                        <Skeleton animation="wave" variant="circular" width={44} height={44} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.08), mb: 2 }} />
                                        <Skeleton animation="wave" width="40%" height={16} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.08), mb: 1 }} />
                                        <Skeleton animation="wave" width="70%" height={28} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.08), mb: 1 }} />
                                        <Skeleton animation="wave" width="50%" height={14} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
                                    </Paper>
                                </Grid>
                            ))
                        ) : (
                            kpiCards.map((card, index) => (
                                <Grid item xs={12} sm={6} md={3} key={index}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            borderRadius: 3,
                                            bgcolor: theme => alpha(theme.palette.primary.main, 0.03),
                                            border: '1px solid',
                                            borderColor: theme => alpha(theme.palette.primary.main, 0.08),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: 2,
                                                bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                                                color: 'primary.main',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 2,
                                            }}
                                        >
                                            {card.icon}
                                        </Box>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.5 }}>
                                            {card.title}
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                                            {card.value}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'grey.500' }}>
                                            {card.subtitle}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))
                        )}
                    </Grid>

                    {/* Charts */}
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        {loading ? (
                            <>
                                <Grid item xs={12} md={5}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: theme => alpha(theme.palette.primary.main, 0.03), border: '1px solid', borderColor: theme => alpha(theme.palette.primary.main, 0.08) }}>
                                        <Skeleton animation="wave" width="50%" height={24} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.08), mb: 2 }} />
                                        <Skeleton animation="wave" variant="rectangular" height={260} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.08), borderRadius: 2 }} />
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={7}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: theme => alpha(theme.palette.primary.main, 0.03), border: '1px solid', borderColor: theme => alpha(theme.palette.primary.main, 0.08) }}>
                                        <Skeleton animation="wave" width="50%" height={24} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.08), mb: 2 }} />
                                        <Skeleton animation="wave" variant="rectangular" height={260} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.08), borderRadius: 2 }} />
                                    </Paper>
                                </Grid>
                            </>
                        ) : (
                            <>
                                {/* Payment Status Donut */}
                                <Grid item xs={12} md={5}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            borderRadius: 3,
                                            bgcolor: theme => alpha(theme.palette.primary.main, 0.03),
                                            border: '1px solid',
                                            borderColor: theme => alpha(theme.palette.primary.main, 0.08),
                                        }}
                                    >
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                                            Payment Status
                                        </Typography>
                                        <Box sx={{ position: 'relative', width: '100%', height: 260 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={donutData}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={70}
                                                        outerRadius={100}
                                                        paddingAngle={3}
                                                        dataKey="value"
                                                        strokeWidth={0}
                                                    >
                                                        <Cell fill={COLORS.success} />
                                                        <Cell fill={COLORS.warning} />
                                                    </Pie>
                                                </PieChart>
                                            </ResponsiveContainer>
                                            {/* Center label */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    textAlign: 'center',
                                                    pointerEvents: 'none',
                                                }}
                                            >
                                                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1 }}>
                                                    {collectionPct}%
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'grey.500' }}>
                                                    collected
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {/* Legend */}
                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1 }}>
                                            {[
                                                { label: `Paid (${paidCount})`, color: COLORS.success },
                                                { label: `Pending (${totalCount - paidCount})`, color: COLORS.warning },
                                            ].map((item, i) => (
                                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} />
                                                    <Typography variant="caption" sx={{ color: 'grey.400' }}>
                                                        {item.label}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Paper>
                                </Grid>

                                {/* Income vs Expenses Bar Chart */}
                                <Grid item xs={12} md={7}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            borderRadius: 3,
                                            bgcolor: theme => alpha(theme.palette.primary.main, 0.03),
                                            border: '1px solid',
                                            borderColor: theme => alpha(theme.palette.primary.main, 0.08),
                                        }}
                                    >
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                                            Income vs Expenses
                                        </Typography>
                                        <ResponsiveContainer width="100%" height={260}>
                                            <BarChart data={monthlyData} barGap={4}>
                                                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} vertical={false} />
                                                <XAxis
                                                    dataKey="month"
                                                    tick={{ fill: COLORS.axis, fontSize: 12 }}
                                                    axisLine={{ stroke: COLORS.grid }}
                                                    tickLine={false}
                                                />
                                                <YAxis
                                                    tick={{ fill: COLORS.axis, fontSize: 12 }}
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
                                                />
                                                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                                                <Bar dataKey="Income" fill={COLORS.success} radius={[4, 4, 0, 0]} maxBarSize={32} />
                                                <Bar dataKey="Expenses" fill={COLORS.expense} radius={[4, 4, 0, 0]} maxBarSize={32} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                        {/* Legend */}
                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1 }}>
                                            {[
                                                { label: 'Income', color: COLORS.success },
                                                { label: 'Expenses', color: COLORS.expense },
                                            ].map((item, i) => (
                                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} />
                                                    <Typography variant="caption" sx={{ color: 'grey.400' }}>
                                                        {item.label}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Paper>
                                </Grid>
                            </>
                        )}
                    </Grid>

                    {/* Footer */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 2,
                            mt: 2,
                            mb: 2,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'grey.500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                            }}
                        >
                            <Box
                                component="a"
                                href="https://garrettflowers.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: 'grey.500',
                                    textDecoration: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    '&:hover': {
                                        color: 'grey.400'
                                    }
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        width: 4,
                                        height: 4,
                                        borderRadius: '50%',
                                        bgcolor: 'grey.500'
                                    }}
                                />
                                Built by Garrett Flowers
                            </Box>
                        </Typography>
                        <IconButton
                            onClick={handleOpenAbout}
                            size="small"
                            sx={{
                                color: 'grey.500',
                                '&:hover': {
                                    color: 'grey.400',
                                    bgcolor: theme => alpha(theme.palette.primary.main, 0.04)
                                }
                            }}
                        >
                            <InfoIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                            onClick={openTermsOfService}
                            size="small"
                            sx={{
                                color: 'grey.500',
                                '&:hover': {
                                    color: 'grey.400',
                                    bgcolor: theme => alpha(theme.palette.primary.main, 0.04)
                                }
                            }}
                        >
                            <DescriptionIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                </MotionBox>
            </Container>

            {/* Minimal Feedback Button */}
            <MotionBox
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.3 }}
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    zIndex: 1000
                }}
            >
                <Box
                    onClick={handleOpenFeedback}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        backgroundColor: 'background.paper',
                        color: 'text.secondary',
                        border: '1px solid',
                        borderColor: 'grey.700',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease-in-out',
                        backdropFilter: 'blur(8px)',
                        '&:hover': {
                            backgroundColor: 'grey.700',
                            borderColor: 'grey.600',
                            color: 'grey.300',
                        }
                    }}
                >
                    <FeedbackIcon sx={{ fontSize: 18 }} />
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            display: { xs: 'none', sm: 'block' }
                        }}
                    >
                        Feedback and Support
                    </Typography>
                </Box>
            </MotionBox>

            <Dialog
                open={openAbout}
                onClose={handleCloseAbout}
                maxWidth="md"
                TransitionComponent={motion.div}
            >
                <DialogTitle
                    sx={{
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            backgroundColor: 'grey.700',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 1,
                        }}
                    >
                        <InfoIcon sx={{ fontSize: 24, color: '#FFFFFF' }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        About the Portal
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: '600px' }}>
                        Everything you need to know about managing your HOA community
                    </Typography>
                    <IconButton
                        onClick={handleCloseAbout}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: 'grey.500',
                            '&:hover': {
                                backgroundColor: 'grey.700',
                                color: 'text.primary'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ mt: 4, pt: 4 }}>
                    <Grid container spacing={4}>
                        {[
                            {
                                title: 'Residents',
                                description: 'Create, edit, and delete resident profiles. Manage all resident account information from a central dashboard.',
                                icon: <PeopleIcon sx={{ fontSize: 24 }} />
                            },
                            {
                                title: 'HOA Invoices',
                                description: 'Generate and manage PDF invoices using resident profile information stored in the database.',
                                icon: <ReceiptIcon sx={{ fontSize: 24 }} />
                            },
                            {
                                title: 'HOA Dues',
                                description: 'Track and manage HOA Dues payments records. All records are sorted by period and stored securely.',
                                icon: <PaymentIcon sx={{ fontSize: 24 }} />
                            },
                            {
                                title: 'Bills Payable',
                                description: 'Manage all neighborhood billing records with easy sorting and filtering options.',
                                icon: <AccountBalanceIcon sx={{ fontSize: 24 }} />
                            },
                            {
                                title: 'Ledger',
                                description: 'View your neighborhood balance and financial overview with detailed transaction history.',
                                icon: <AccountBalanceIcon sx={{ fontSize: 24 }} />
                            }
                        ].map((item, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <Box sx={{
                                    p: 3,
                                    backgroundColor: 'background.default',
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'grey.700',
                                    height: '100%'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Box sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: 1.5,
                                            backgroundColor: 'grey.700',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 2
                                        }}>
                                            {item.icon}
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {item.title}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {item.description}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions
                    sx={{
                        p: 3,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Button
                        onClick={handleCloseAbout}
                        variant="outlined"
                        sx={{
                            borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                            color: 'text.primary',
                            px: 4,
                            '&:hover': {
                                borderColor: 'primary.main',
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                            },
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openFeedback}
                onClose={handleCloseFeedback}
                maxWidth="sm"
                fullWidth
                TransitionComponent={motion.div}
                sx={{
                    '& .MuiDialog-container': {
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh'
                    },
                    '& .MuiDialog-paper': {
                        margin: 0,
                        position: 'static'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        p: 3,
                        pb: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            backgroundColor: 'grey.700',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 1,
                        }}
                    >
                        <FeedbackIcon sx={{ fontSize: 24, color: '#FFFFFF' }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Report Issue or Share Feedback
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: '400px' }}>
                        Found a bug? Have a suggestion? Let us know how we can improve your experience with the HOA management portal.
                    </Typography>
                    <IconButton
                        onClick={handleCloseFeedback}
                        disabled={isSubmittingFeedback}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: 'grey.500',
                            '&:hover': {
                                backgroundColor: 'grey.700',
                                color: 'text.primary'
                            },
                            '&:disabled': {
                                color: 'grey.600',
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        p: 3,
                        pt: 4,
                        mt: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <TextField
                        autoFocus
                        multiline
                        rows={5}
                        value={feedback}
                        onChange={(e) => {
                            if (e.target.value.length <= 1000) {
                                setFeedback(e.target.value);
                            }
                        }}
                        placeholder="Describe the issue you encountered or share your feedback/suggestions..."
                        fullWidth
                        variant="outlined"
                    />
                    <Typography variant="caption" sx={{ color: 'grey.500', textAlign: 'right' }}>
                        {feedback.length} / 1000 characters
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        p: 3,
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 2,
                    }}
                >
                    <Button
                        onClick={handleCloseFeedback}
                        variant="outlined"
                        disabled={isSubmittingFeedback}
                        sx={{
                            borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                            color: 'text.primary',
                            px: 4,
                            '&:hover': {
                                borderColor: 'primary.main',
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                            },
                            '&:disabled': {
                                borderColor: 'grey.700',
                                color: 'grey.500',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmitFeedback}
                        variant="contained"
                        disabled={isSubmittingFeedback}
                        sx={{
                            backgroundColor: 'grey.700',
                            color: 'text.primary',
                            px: 4,
                            '&:hover': {
                                backgroundColor: 'grey.600',
                            },
                            '&:disabled': {
                                backgroundColor: 'grey.700',
                                color: 'grey.500',
                            },
                        }}
                        startIcon={isSubmittingFeedback ? <CircularProgress size={16} sx={{ color: 'grey.500' }} /> : null}
                    >
                        {isSubmittingFeedback ? 'Submitting...' : 'Submit Report'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{
                        backgroundColor: '#1a1a1b',
                        color: '#FFFFFF',
                        border: '1px solid',
                        borderColor: 'grey.700',
                        '& .MuiAlert-icon': {
                            color: snackbar.severity === 'success' ? 'success.main' :
                                   snackbar.severity === 'error' ? 'error.main' : 'warning.main'
                        }
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
        </>
    );
}

export default Home;
